import React, { useMemo } from 'react';
import VisualCard from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList/VisualCard';
import {
  allMetricsOptions,
  IMetricsOptionItem,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import useMetricGameData from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList/hooks/useMetricGameData';
import useLazyLoadVisualCardList from '@intelligence/pages/Compare/PcConsoleGamesBeta/components/VisualList/hooks/useLazyLoadVisualCardList';
import { SelectGames } from '@intelligence/components/AllGameSelector/types';

import './index.scss';

export interface IMetricValue {
  id: string;
  metric: string;
  date: string;
  value: number;
}

export type TMetricValueList = IMetricValue[];

interface IGamesMetricDataItem {
  id: string;
  name: string;
  cover: string;
  metric_value_list: TMetricValueList;
}

export type TGamesMetricData = IGamesMetricDataItem[];

interface IVisualListProps {
  fetchParams: any;
  // 当前选中的游戏列表，用户展示对应游戏，过滤掉表格删除的游戏
  compareGameList: SelectGames[];
  // 所有游戏指标按日期分组全量数据
  gamesMetricData: TGamesMetricData;
  // 游戏指标聚合数据，表格展示数据，用于给指标对应的游戏数据排序
  gamesMetricDataAggregation: TGamesMetricData;
  // 是否是 align to release, 用于区别展示可视化 x 轴 label
  isAlignToRelease: boolean;
  // 返回的查询时间，用于空数据补全
  responseQueryDate?: { startDate: string | number; endDate: string | number };
  // 宽表游戏对应
  gamesColorMap?: { [key: string]: string };
  // 加载中
  loading?: boolean;
}

function VisualList({
  gamesMetricData,
  compareGameList,
  gamesMetricDataAggregation,
  fetchParams,
  responseQueryDate,
  isAlignToRelease,
  loading,
  gamesColorMap,
}: IVisualListProps) {
  // 当前时间粒度
  const dateType = fetchParams?.granularity;

  // 请求的所有指标项
  const fetchParamsMetrics: IMetricsOptionItem[] = useMemo(() => {
    const { source_metrics: sourceMetrics = {} } = fetchParams || {};
    const objValue = Object.values(sourceMetrics).flat();
    return allMetricsOptions.filter((item: any) => {
      return objValue.includes(item?.value);
    });
  }, [fetchParams]);

  // 当前勾选,图表需要渲染的游戏 id
  const compareGameIds = useMemo(() => compareGameList?.map((item) => item?.id), [compareGameList]);
  // 数据处理
  const metricDataList = useMetricGameData({
    gamesMetricData,
    gamesMetricDataAggregation,
    fetchParamsMetrics,
    isAlignToRelease,
    compareGameIds,
    dateType,
    responseQueryDate,
    gamesColorMap,
  });
  // 懒加载数据拆分
  const metricLazyLoadDataList = useLazyLoadVisualCardList(metricDataList);

  const needRepleaceMetricsValue = [
    'ampere_d1',
    'ampere_d7',
    'ampere_d14',
    'ampere_d28',
    'ampere_d60',
    'ampere_rolling_d1',
    'ampere_rolling_d7',
    'ampere_rolling_d14',
    'ampere_rolling_d28',
    'ampere_rolling_d60',
  ];

  const isMonth = dateType === 'monthly';
  const middle = isMonth ? 'monthly_' : '';
  const splitor = 'dbi_metric_';

  // console.debug('metricLazyLoadDataList', { metricLazyLoadDataList, metricDataList });

  return (
    <div className="dbi_compare_visual_list_warp">
      {metricLazyLoadDataList?.map((item, index) => {
        const textStrArr = item?.text?.split(splitor);
        const rawMetricText = textStrArr?.[textStrArr?.length - 1];

        const metricTextKey =
          isMonth && needRepleaceMetricsValue.includes(item?.metric)
            ? `${splitor}${middle}${rawMetricText}`
            : item?.text;

        return (
          <VisualCard
            key={index}
            dateType={dateType}
            // metricTextKey={item.text}
            metricTextKey={metricTextKey}
            colors={item.colors}
            lineData={item.gamesData}
            loading={Boolean(loading)}
            icon={item.icon}
            dataSource={item.dataSource}
            isPercentage={item.isPercentage}
            isAlignToRelease={isAlignToRelease}
            isSentinel={
              index === metricLazyLoadDataList.length - 1 && index !== metricDataList.length - 1
            }
          />
        );
      })}
    </div>
  );
}

export default VisualList;
